/**
 * Text-driven sticker silhouette engine (ported from dynamic-text-highlight_1.html).
 *
 * Measures rendered line boxes in the DOM, regularizes them, and emits one SVG path
 * that hugs the text — including concave/convex corners between lines of different width.
 */

import type { PadShelfYByLine, ShelfPadding } from './stickerSizePresets';

export interface LineRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface StickerBuildOptions {
  radius: number;
  padX: number;
  padY: number;
  /** Extra vertical padding on lines that protrude relative to their neighbour. */
  padShelfY?: number | PadShelfYByLine;
  /** When false, skip bottom shelf padding on the final line and trim descender slack. */
  hasLabel?: boolean;
  snap: number;
  fontSize: number;
}

export const shelfPaddingForLine = (index: number, lineCount: number, padShelfY: PadShelfYByLine): ShelfPadding => {
  if (lineCount <= 1) return padShelfY.first;
  if (index === 0) return padShelfY.first;
  if (index === lineCount - 1) return padShelfY.last;
  return padShelfY.middle;
};

export interface StickerShapeResult {
  path: string;
  viewWidth: number;
  viewHeight: number;
  gradient: { x1: number; y1: number; x2: number; y2: number };
}

/** Measure rendered text lines relative to `baseElement`. */
export const measureLines = (element: HTMLElement, baseElement: HTMLElement): LineRect[] => {
  const collected: DOMRect[] = [];

  const collect = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      const wordPattern = /\S+/g;
      let match: RegExpExecArray | null;

      while ((match = wordPattern.exec(text))) {
        try {
          const range = document.createRange();
          range.setStart(node, match.index);
          range.setEnd(node, match.index + match[0].length);

          let rects: DOMRect[];
          if (typeof range.getClientRects === 'function') {
            rects = Array.from(range.getClientRects());
          } else if (typeof range.getBoundingClientRect === 'function') {
            rects = [range.getBoundingClientRect()];
          } else {
            rects = [];
          }

          collected.push(...rects.filter(rect => rect.width > 0 && rect.height > 0));
        } catch {
          // jsdom and some test environments lack full Range geometry APIs.
        }
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const htmlNode = node as HTMLElement;
    const display = getComputedStyle(htmlNode).display;
    if (display === 'none') return;

    if (display.startsWith('inline-') || htmlNode.tagName === 'IMG') {
      collected.push(htmlNode.getBoundingClientRect());
      return;
    }

    htmlNode.childNodes.forEach(collect);
  };

  element.childNodes.forEach(collect);

  const base = baseElement.getBoundingClientRect();
  const raw = collected.filter(rect => rect.width > 1 && rect.height > 2).sort((a, b) => a.top - b.top);

  const lines: LineRect[] = [];

  for (const rect of raw) {
    const line: LineRect = {
      top: rect.top - base.top,
      bottom: rect.bottom - base.top,
      left: rect.left - base.left,
      right: rect.right - base.left,
    };

    const current = lines.at(-1);
    if (current && line.top < (current.top + current.bottom) / 2) {
      current.left = Math.min(current.left, line.left);
      current.right = Math.max(current.right, line.right);
      current.top = Math.min(current.top, line.top);
      current.bottom = Math.max(current.bottom, line.bottom);
    } else {
      lines.push(line);
    }
  }

  if (lines.length === 0) {
    const fallback = element.getBoundingClientRect();
    if (fallback.width > 0 && fallback.height > 0) {
      return [
        {
          top: fallback.top - base.top,
          bottom: fallback.bottom - base.top,
          left: fallback.left - base.left,
          right: fallback.right - base.left,
        },
      ];
    }
  }

  return lines;
};

/** Close vertical gaps between consecutive line boxes. */
export const closeGaps = (lines: LineRect[]) => {
  for (let index = 0; index < lines.length - 1; index += 1) {
    const mid = (lines[index].bottom + lines[index + 1].top) / 2;
    lines[index].bottom = mid;
    lines[index + 1].top = mid;
  }
};

/** Snap near-equal left/right edges so corner arcs have room. */
export const snapEdges = (lines: LineRect[], threshold: number) => {
  if (threshold <= 0) return;

  const align = (side: 'left' | 'right', pick: (a: number, b: number) => number) => {
    let start = 0;

    for (let index = 1; index <= lines.length; index += 1) {
      if (index === lines.length || Math.abs(lines[index][side] - lines[index - 1][side]) > threshold) {
        const value = lines.slice(start, index).reduce((acc, line) => pick(acc, line[side]), lines[start][side]);
        for (let groupIndex = start; groupIndex < index; groupIndex += 1) {
          lines[groupIndex][side] = value;
        }
        start = index;
      }
    }
  };

  align('right', Math.max);
  align('left', Math.min);
};

/** Raise inset edges so the silhouette never carves valleys between lines. */
export const fillValleys = (lines: LineRect[]) => {
  const count = lines.length;
  if (count < 3) return;

  const prefixMaxRight: number[] = [];
  const suffixMaxRight: number[] = [];
  let maxRight = -Infinity;

  for (let index = 0; index < count; index += 1) {
    maxRight = Math.max(maxRight, lines[index].right);
    prefixMaxRight[index] = maxRight;
  }

  maxRight = -Infinity;
  for (let index = count - 1; index >= 0; index -= 1) {
    maxRight = Math.max(maxRight, lines[index].right);
    suffixMaxRight[index] = maxRight;
  }

  for (let index = 0; index < count; index += 1) {
    lines[index].right = Math.max(lines[index].right, Math.min(prefixMaxRight[index], suffixMaxRight[index]));
  }

  const prefixMinLeft: number[] = [];
  const suffixMinLeft: number[] = [];
  let minLeft = Infinity;

  for (let index = 0; index < count; index += 1) {
    minLeft = Math.min(minLeft, lines[index].left);
    prefixMinLeft[index] = minLeft;
  }

  minLeft = Infinity;
  for (let index = count - 1; index >= 0; index -= 1) {
    minLeft = Math.min(minLeft, lines[index].left);
    suffixMinLeft[index] = minLeft;
  }

  for (let index = 0; index < count; index += 1) {
    lines[index].left = Math.min(lines[index].left, Math.max(prefixMinLeft[index], suffixMinLeft[index]));
  }
};

export const regularize = (lines: LineRect[], threshold: number) => {
  for (let iteration = 0; iteration < 4; iteration += 1) {
    const before = JSON.stringify(lines);
    snapEdges(lines, threshold);
    fillValleys(lines);
    if (JSON.stringify(lines) === before) break;
  }
};

export const vertices = (lines: LineRect[]): [number, number][] => {
  const points: [number, number][] = [];
  const first = lines[0];
  const last = lines.at(-1)!;

  points.push([first.left, first.top], [first.right, first.top]);

  for (let index = 0; index < lines.length - 1; index += 1) {
    const current = lines[index];
    const next = lines[index + 1];
    if (Math.abs(next.right - current.right) > 0.5) {
      const stepY = (current.bottom + next.top) / 2;
      points.push([current.right, stepY], [next.right, stepY]);
    }
  }

  points.push([last.right, last.bottom], [last.left, last.bottom]);

  for (let index = lines.length - 1; index > 0; index -= 1) {
    const current = lines[index];
    const previous = lines[index - 1];
    if (Math.abs(previous.left - current.left) > 0.5) {
      const stepY = (previous.bottom + current.top) / 2;
      points.push([current.left, stepY], [previous.left, stepY]);
    }
  }

  return points;
};

export const roundedPath = (points: [number, number][], radius: number): string => {
  const count = points.length;
  let path = '';

  for (let index = 0; index < count; index += 1) {
    const previous = points[(index - 1 + count) % count];
    const current = points[index];
    const next = points[(index + 1) % count];

    const incoming = [current[0] - previous[0], current[1] - previous[1]];
    const outgoing = [next[0] - current[0], next[1] - current[1]];
    const incomingLength = Math.hypot(incoming[0], incoming[1]);
    const outgoingLength = Math.hypot(outgoing[0], outgoing[1]);

    if (incomingLength < 0.01 || outgoingLength < 0.01) continue;

    const cornerRadius = Math.min(radius, incomingLength / 2, outgoingLength / 2);
    const unitIncoming = [incoming[0] / incomingLength, incoming[1] / incomingLength];
    const unitOutgoing = [outgoing[0] / outgoingLength, outgoing[1] / outgoingLength];
    const arcStart = [current[0] - unitIncoming[0] * cornerRadius, current[1] - unitIncoming[1] * cornerRadius];
    const arcEnd = [current[0] + unitOutgoing[0] * cornerRadius, current[1] + unitOutgoing[1] * cornerRadius];
    const sweep = unitIncoming[0] * unitOutgoing[1] - unitIncoming[1] * unitOutgoing[0] > 0 ? 1 : 0;

    path +=
      index === 0
        ? `M${arcStart[0].toFixed(2)} ${arcStart[1].toFixed(2)}`
        : `L${arcStart[0].toFixed(2)} ${arcStart[1].toFixed(2)}`;
    path += `A${cornerRadius.toFixed(2)} ${cornerRadius.toFixed(2)} 0 0 ${sweep} ${arcEnd[0].toFixed(2)} ${arcEnd[1].toFixed(2)}`;
  }

  return `${path}Z`;
};

/** Resolve the raw (pre-scaling) shelf padding for a title line. */
const resolveShelfForLine = (
  padShelfY: number | PadShelfYByLine,
  index: number,
  titleLineCount: number,
): ShelfPadding =>
  typeof padShelfY === 'number'
    ? { top: padShelfY, bottom: padShelfY }
    : shelfPaddingForLine(index, titleLineCount, padShelfY);

/** Apply the special-cased shelf scaling used for three- and four-line titles. */
const scaleShelfForTitleLineCount = (shelf: ShelfPadding, index: number, titleLineCount: number): ShelfPadding => {
  if (titleLineCount === 3) {
    // Tighten first/last shelves, keep the second (middle) line fuller.
    const shelfScale = index === 1 ? 1 : 0.55;
    return { top: Math.round(shelf.top * shelfScale), bottom: Math.round(shelf.bottom * shelfScale) };
  }

  if (titleLineCount === 4 && index === titleLineCount - 1) {
    // Modest top shelf / no bottom shelf on the last title line.
    return { top: Math.round(Math.max(shelf.top + 10, shelf.top * 2.25) / 2), bottom: 0 };
  }

  return shelf;
};

/** Grow the shelf line and its neighbour on both sides of a protruding line. */
const applyTopAndBottomShelf = (
  lines: LineRect[],
  index: number,
  shelf: ShelfPadding,
  options: StickerBuildOptions,
  isLastLine: boolean,
) => {
  if (shelf.top > 0 && index > 0) {
    lines[index - 1].bottom -= shelf.top;
    lines[index].top = lines[index - 1].bottom;
  }

  if (shelf.bottom > 0 && (options.hasLabel !== false || !isLastLine)) {
    lines[index].bottom += shelf.bottom;
    if (index + 1 < lines.length) {
      lines[index + 1].top = lines[index].bottom;
    }
  }
};

/** Grow a shelf line toward whichever neighbour(s) it protrudes past. */
const applyLineShelf = (
  lines: LineRect[],
  measured: LineRect[],
  index: number,
  shelf: ShelfPadding,
  options: StickerBuildOptions,
  titleLineCount: number,
) => {
  const isLastTitleLine = index === titleLineCount - 1;
  const isLastLine = index === measured.length - 1;
  const protrudesPastAbove = index > 0 && measured[index].right > measured[index - 1].right + 0.5;
  const protrudesPastBelow = index < measured.length - 1 && measured[index].right > measured[index + 1].right + 0.5;

  if (protrudesPastAbove || (isLastTitleLine && protrudesPastBelow && shelf.top > 0)) {
    applyTopAndBottomShelf(lines, index, shelf, options, isLastLine);
    return;
  }

  if (protrudesPastBelow && shelf.bottom > 0) {
    lines[index].bottom += shelf.bottom;
    lines[index + 1].top = lines[index].bottom;
  }
};

/** Expand shelf lines (protruding lines) by the configured vertical padding. */
const applyShelfPadding = (lines: LineRect[], measured: LineRect[], options: StickerBuildOptions) => {
  if (!options.padShelfY) return;

  // With a label, the last measured box is the pill — map first/middle/last to title lines only.
  const hasLabel = options.hasLabel === true;
  const titleLineCount = hasLabel ? Math.max(0, measured.length - 1) : measured.length;

  for (let index = 0; index < measured.length; index += 1) {
    // Label silhouette padding is handled by the caller; skip title shelf roles for it.
    if (hasLabel && index === measured.length - 1) continue;

    const rawShelf = resolveShelfForLine(options.padShelfY, index, titleLineCount);
    const shelf = scaleShelfForTitleLineCount(rawShelf, index, titleLineCount);
    if (shelf.top <= 0 && shelf.bottom <= 0) continue;

    applyLineShelf(lines, measured, index, shelf, options, titleLineCount);
  }
};

export const buildStickerShape = (
  measured: LineRect[],
  options: StickerBuildOptions,
  viewWidth: number,
  viewHeight: number,
): StickerShapeResult | null => {
  if (measured.length === 0) return null;

  const capTrim = Math.round(options.fontSize * 0.12);

  const lines = measured.map(line => ({
    ...line,
    left: line.left - options.padX,
    right: line.right + options.padX,
    top: line.top - options.padY,
    bottom: line.bottom + options.padY,
  }));

  closeGaps(lines);

  // Ascender headroom: without this trim the top margin reads larger than the sides.
  lines[0].top += capTrim;

  if (options.hasLabel === false) {
    // Match the top trim so title-only cards do not read with extra bottom padding.
    lines.at(-1)!.bottom -= capTrim;
  } else {
    // Give the label shelf a bit more room on the right and bottom of the silhouette.
    const labelPadExtra = Math.max(6, Math.round(options.padX * 0.25));
    lines.at(-1)!.right += labelPadExtra;
    lines.at(-1)!.bottom += labelPadExtra;
  }

  applyShelfPadding(lines, measured, options);

  const threshold = Math.max(options.snap, options.radius * 2 + 2, options.fontSize * 0.75);
  regularize(lines, threshold);

  const minX = Math.min(...lines.map(line => line.left));
  const maxX = Math.max(...lines.map(line => line.right));
  const minY = lines[0].top;
  const maxY = lines.at(-1)!.bottom;

  return {
    path: roundedPath(vertices(lines), options.radius),
    viewWidth,
    viewHeight,
    gradient: { x1: minX, y1: minY, x2: maxX, y2: maxY },
  };
};
